import { Id, MessageContent } from '@nextforce/api'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useServer } from 'app/server/context'
import { useChatConfig } from 'chat/config/queries/get'
import { addConversationToCacheIfNeeded } from 'chat/conversations/queries/shared/utils'
import { getMessageForm } from 'chat/messages/queries/create'
import { addMessagesDataToCache } from 'chat/messages/queries/shared/utils'
import { FileUploadStatus } from 'chat/send_message_context'
import { useChatWebsocketContext } from 'chat/websocket/context'
import { convertComposeMessageEvent } from './shared/convert'
import * as Response from './shared/response'
import { ComposeMessageEvent } from './shared/types'

type Variables = {
  content: MessageContent
  userIds: number[]
  groupIds: number[]
  storeId: Id | undefined
  messageManagers?: boolean
  isUrgent?: boolean
  setFileUploadStatus?: (value: FileUploadStatus) => void
}

export function useCreateComposeMessage() {
  const { server } = useServer()
  const client = useQueryClient()
  const { data: chatConfig } = useChatConfig()
  const { sendWebsocketMessage } = useChatWebsocketContext()

  return useMutation({
    mutationFn: createComposeMessage,
    onSuccess: async composeMessageEvent => {
      if (composeMessageEvent) {
        const addMessageEvents = composeMessageEvent.addMessageEvents
        const conversation = composeMessageEvent.conversation
        if (conversation) {
          await addConversationToCacheIfNeeded(client, conversation)
        }
        if (addMessageEvents) {
          await addMessagesDataToCache(client, addMessageEvents, chatConfig)

          sendWebsocketMessage({
            action: 'sendComposeMessageEvents',
            messageEvents: addMessageEvents,
            conversationEvent: conversation,
          })
        }
      }
    },
  })

  async function createComposeMessage({
    content,
    userIds,
    groupIds,
    storeId,
    messageManagers = false,
    isUrgent = false,
    setFileUploadStatus,
  }: Variables): Promise<ComposeMessageEvent | undefined> {
    if (!storeId) {
      throw new Error('Illegal store ID')
    }

    const responseConversation =
      await server.post<Response.ComposeMessageEvent>(
        `/v2/chat/compose/?store_id=${storeId}`,
        await getComposeForm(),
        'user',
        'multipart/form-data',
        progressEvent =>
          setFileUploadStatus &&
          setFileUploadStatus({
            progress: progressEvent.loaded / (progressEvent?.total ?? 1),
          })
      )
    return convertComposeMessageEvent(responseConversation)

    async function getComposeForm() {
      const form = await getMessageForm(content)
      form.append('users', userIds.toString())
      form.append('groups', groupIds.toString())
      form.append('message_managers', messageManagers.toString())
      form.append('urgent', isUrgent.toString())
      return form
    }
  }
}
